import {
  Bookmark, CalendarDays, Check, Clock3, Download, FileArchive, Link2, Pin, PinOff, Printer, Save, Trash2, X,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { MultiSelect, type MultiOption } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import type { ReportExport } from "@/features/report-kit";
import { saveBlob } from "@/lib/api";
import {
  GRANULARITIES, KIND_LABELS, MODE_LABELS, RANGE_PRESETS, WEEKDAY_NAMES, exportName, hourName, rangeLabel,
  todayIn, zipFiles, zoneChoices, type CallKind, type CompareMode, type DialMode, type Granularity,
  type ReportFilters, type ReportTab, type SavedView,
} from "@/lib/reports";
import type { ReportOptions } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The one row of controls above a report (the dataviz rule: filters in one place, scoping
 * everything under them). The window comes first because it is what everybody reaches for; the
 * narrowing filters differ by tab, since "skill" means the caller's need on the line report and
 * the rep's ability on the rep report.
 */

type Setter = (next: ReportFilters) => void;

// --- the window --------------------------------------------------------------------------------

function DateRangeControl({ filters, onChange }: { filters: ReportFilters; onChange: Setter }) {
  const [open, setOpen] = React.useState(false);
  const today = todayIn(filters.tz);
  const [from, setFrom] = React.useState(filters.from ?? today);
  const [to, setTo] = React.useState(filters.to ?? today);
  React.useEffect(() => {
    if (open) {
      setFrom(filters.from ?? today);
      setTo(filters.to ?? today);
    }
  }, [open, filters.from, filters.to, today]);
  const valid = Boolean(from && to && from <= to && to <= today);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-sm font-medium text-text shadow-xs hover:bg-surface-hover"
        >
          <CalendarDays className="size-4 text-text-subtle" aria-hidden />
          {rangeLabel(filters)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1" align="start">
        <ul role="listbox" aria-label="Report window">
          {RANGE_PRESETS.map((p) => {
            const on = filters.range === p.id;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => {
                    onChange({ ...filters, range: p.id, from: null, to: null });
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-text hover:bg-surface-hover"
                >
                  <span className="flex size-4 items-center justify-center">
                    {on && <Check className="size-4 text-accent-11" strokeWidth={3} />}
                  </span>
                  {p.label}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mt-1 space-y-2 border-t border-border p-2">
          <p className="text-2xs font-medium uppercase tracking-wide text-text-subtle">Custom range</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1 text-xs text-text-muted">
              From
              <Input type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} className="text-sm" />
            </label>
            <label className="space-y-1 text-xs text-text-muted">
              To
              <Input type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} className="text-sm" />
            </label>
          </div>
          <Button
            size="sm"
            variant="default"
            className="w-full"
            disabled={!valid}
            onClick={() => {
              onChange({ ...filters, range: "custom", from, to });
              setOpen(false);
            }}
          >
            Show these days
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const WEEKDAY_SETS: readonly { label: string; days: number[] }[] = [
  { label: "Every day", days: [] },
  { label: "Weekdays", days: [1, 2, 3, 4, 5] },
  { label: "Weekends", days: [6, 7] },
];
const HOUR_SETS: readonly { label: string; from: number | null; to: number | null }[] = [
  { label: "All day", from: null, to: null },
  { label: "9 to 5", from: 9, to: 17 },
  { label: "Calling hours, 8a–9p", from: 8, to: 21 },
];

function hoursSummary(f: ReportFilters): string | null {
  const parts: string[] = [];
  if (f.weekdays.length) {
    const set = WEEKDAY_SETS.find((s) => s.days.join() === [...f.weekdays].sort().join());
    parts.push(set ? set.label : f.weekdays.map((d) => WEEKDAY_NAMES[d - 1]!.slice(0, 3)).join(", "));
  }
  if (f.hourFrom !== null && f.hourTo !== null) parts.push(`${hourName(f.hourFrom)}–${hourName(f.hourTo % 24)}`);
  return parts.length ? parts.join(" · ") : null;
}

function HoursControl({ filters, onChange }: { filters: ReportFilters; onChange: Setter }) {
  const summary = hoursSummary(filters);
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const toggleDay = (d: number) => {
    const set = new Set(filters.weekdays);
    if (set.has(d)) set.delete(d);
    else set.add(d);
    const days = [...set].sort();
    onChange({ ...filters, weekdays: days.length === 7 ? [] : days });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Days and hours: ${summary ?? "all"}`}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm shadow-xs",
            summary
              ? "border-accent-7 bg-accent-2 text-accent-12 hover:bg-accent-3"
              : "border-border bg-surface text-text hover:bg-surface-hover",
          )}
        >
          <Clock3 className="size-4 text-text-subtle" aria-hidden />
          <span className="text-text-muted">Hours</span>
          <span className="font-medium">{summary ?? "All"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3 p-3" align="start">
        <div className="space-y-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-text-subtle">Days</p>
          <div className="flex gap-1" role="group" aria-label="Days of the week">
            {WEEKDAY_NAMES.map((name, i) => {
              const d = i + 1;
              const on = filters.weekdays.length === 0 || filters.weekdays.includes(d);
              return (
                <button
                  key={name}
                  type="button"
                  aria-pressed={on}
                  aria-label={name}
                  onClick={() => toggleDay(d)}
                  className={cn(
                    "size-8 rounded-md border text-xs font-medium",
                    on ? "border-accent-7 bg-accent-3 text-accent-11" : "border-border bg-surface text-text-muted hover:bg-surface-hover",
                  )}
                >
                  {name.slice(0, 2)}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 text-xs">
            {WEEKDAY_SETS.map((s) => (
              <button key={s.label} type="button" className="text-accent-11 hover:underline" onClick={() => onChange({ ...filters, weekdays: s.days })}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-text-subtle">Hours, on the report's clock</p>
          <div className="flex items-center gap-2">
            <Select
              value={filters.hourFrom === null ? "all" : String(filters.hourFrom)}
              onValueChange={(v) =>
                onChange(
                  v === "all"
                    ? { ...filters, hourFrom: null, hourTo: null }
                    : { ...filters, hourFrom: Number(v), hourTo: filters.hourTo ?? 24 },
                )
              }
            >
              <SelectTrigger className="w-28" aria-label="From">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">From any</SelectItem>
                {hours.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    From {hourName(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.hourTo === null ? "all" : String(filters.hourTo)}
              onValueChange={(v) =>
                onChange(
                  v === "all"
                    ? { ...filters, hourFrom: null, hourTo: null }
                    : { ...filters, hourFrom: filters.hourFrom ?? 0, hourTo: Number(v) },
                )
              }
            >
              <SelectTrigger className="w-28" aria-label="Until">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Until any</SelectItem>
                {hours.map((h) => (
                  <SelectItem key={h + 1} value={String(h + 1)} disabled={h + 1 === filters.hourFrom}>
                    Until {hourName((h + 1) % 24)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {HOUR_SETS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="text-accent-11 hover:underline"
                onClick={() => onChange({ ...filters, hourFrom: s.from, hourTo: s.to })}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-text-muted">
            A call counts by when it started; a rep's time is cut to these hours. An evening range can
            run past midnight (9p–6a).
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- the narrowing filters ---------------------------------------------------------------------

export function campaignOptions(options: ReportOptions | undefined): MultiOption[] {
  return (options?.campaigns ?? []).map((c) => ({
    value: c.id,
    label: c.name,
    hint: [c.purpose === "both" ? "Inbound and outbound" : c.purpose === "inbound" ? "Inbound" : "Outbound", c.status]
      .filter(Boolean)
      .join(" · "),
  }));
}

function lineOptions(options: ReportOptions | undefined): MultiOption[] {
  const names = new Map((options?.campaigns ?? []).map((c) => [c.id, c.name]));
  return (options?.lines ?? [])
    .map((l) => ({
      value: l.name,
      label: l.description || l.name,
      hint: l.description ? l.name : l.purpose ?? undefined,
      group: l.campaign_id ? (names.get(l.campaign_id) ?? "Other campaigns") : "Lines of no campaign",
    }))
    .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
}

export function repOptions(options: ReportOptions | undefined): MultiOption[] {
  const skills = new Map((options?.skills ?? []).map((s) => [s.slug, s.name]));
  return (options?.reps ?? []).map((r) => ({
    value: `${r.user}@${r.domain}`,
    label: r.name ?? r.user,
    hint: [r.user, ...r.skills.map((s) => skills.get(s) ?? s)].join(" · "),
    keywords: `${r.domain} ${r.role ?? ""}`,
    group: r.active ? undefined : "No longer active",
  }));
}

function teamOptions(options: ReportOptions | undefined): MultiOption[] {
  return (options?.teams ?? []).map((t) => ({
    value: String(t.id),
    label: t.name,
    hint: t.members === 1 ? "1 person" : `${t.members} people`,
  }));
}

function skillOptions(options: ReportOptions | undefined): MultiOption[] {
  return (options?.skills ?? []).map((s) => ({ value: s.slug, label: s.name, hint: s.category ?? undefined }));
}

function firmOptions(options: ReportOptions | undefined): MultiOption[] {
  return (options?.firms ?? []).map((f) => ({ value: String(f.id), label: f.label, hint: f.status }));
}

const KIND_OPTIONS: MultiOption[] = (["inbound", "outbound", "handoff"] as const).map((k) => ({
  value: k,
  label: KIND_LABELS[k]!,
  hint: k === "inbound" ? "A caller rang a number" : k === "outbound" ? "The dialer reached a lead" : "A setter sent a caller on",
}));
const MODE_OPTIONS: MultiOption[] = (["power", "preview", "manual"] as const).map((m) => ({
  value: m,
  label: MODE_LABELS[m]!,
  hint: m === "power" ? "The dialer dials; a rep gets the answer" : m === "preview" ? "The rep sees the lead, then dials" : "A number the rep typed",
}));

/** What each tab lets you narrow by, and what the words mean there. A team stands for the reps in
 *  it now, so it goes wherever reps do. */
const TAB_FILTERS: Record<
  ReportTab,
  readonly ("campaigns" | "lines" | "teams" | "reps" | "skills" | "kinds" | "modes" | "firms")[]
> = {
  lines: ["campaigns", "lines", "kinds", "skills"],
  campaigns: ["campaigns", "firms", "modes", "teams", "reps"],
  reps: ["teams", "reps", "skills", "campaigns", "lines", "kinds", "modes"],
};

function DimensionFilters({
  tab, filters, onChange, options,
}: {
  tab: ReportTab;
  filters: ReportFilters;
  onChange: Setter;
  options: ReportOptions | undefined;
}) {
  const wanted = TAB_FILTERS[tab];
  return (
    <>
      {wanted.includes("campaigns") && (
        <MultiSelect label="Campaigns" options={campaignOptions(options)} value={filters.campaigns} onChange={(v) => onChange({ ...filters, campaigns: v })} />
      )}
      {wanted.includes("lines") && (
        <MultiSelect label="Lines" options={lineOptions(options)} value={filters.lines} onChange={(v) => onChange({ ...filters, lines: v })} />
      )}
      {wanted.includes("teams") && (
        <MultiSelect
          label="Teams"
          options={teamOptions(options)}
          value={filters.teams.map(String)}
          onChange={(v) => onChange({ ...filters, teams: v.map(Number) })}
        />
      )}
      {wanted.includes("reps") && (
        <MultiSelect label="Reps" options={repOptions(options)} value={filters.reps} onChange={(v) => onChange({ ...filters, reps: v })} />
      )}
      {wanted.includes("skills") && (
        <MultiSelect
          label={tab === "reps" ? "Rep skill" : "Caller needs"}
          options={skillOptions(options)}
          value={filters.skills}
          onChange={(v) => onChange({ ...filters, skills: v })}
        />
      )}
      {wanted.includes("firms") && (
        <MultiSelect label="Firms" options={firmOptions(options)} value={filters.firms.map(String)} onChange={(v) => onChange({ ...filters, firms: v.map(Number) })} />
      )}
      {wanted.includes("kinds") && (
        <MultiSelect label="Call type" options={KIND_OPTIONS} value={filters.kinds} onChange={(v) => onChange({ ...filters, kinds: v as CallKind[] })} />
      )}
      {wanted.includes("modes") && (
        <MultiSelect label="Dial mode" options={MODE_OPTIONS} value={filters.modes} onChange={(v) => onChange({ ...filters, modes: v as DialMode[] })} />
      )}
    </>
  );
}

/** Every narrowing filter as a chip with its own ×, so one can go without redoing the rest. */
function ActiveChips({
  tab, filters, onChange, options, onReset,
}: {
  tab: ReportTab;
  filters: ReportFilters;
  onChange: Setter;
  options: ReportOptions | undefined;
  onReset: () => void;
}) {
  const name = (list: MultiOption[], value: string) => list.find((o) => o.value === value)?.label ?? value;
  const wanted = TAB_FILTERS[tab];
  const chips: { key: string; label: string; remove: () => void }[] = [];
  const add = (kind: string, values: readonly string[], list: MultiOption[], set: (rest: string[]) => ReportFilters) => {
    for (const v of values) {
      chips.push({ key: `${kind}:${v}`, label: `${kind}: ${name(list, v)}`, remove: () => onChange(set(values.filter((x) => x !== v))) });
    }
  };
  if (wanted.includes("campaigns")) add("Campaign", filters.campaigns, campaignOptions(options), (rest) => ({ ...filters, campaigns: rest }));
  if (wanted.includes("lines")) add("Line", filters.lines, lineOptions(options), (rest) => ({ ...filters, lines: rest }));
  if (wanted.includes("teams")) {
    add("Team", filters.teams.map(String), teamOptions(options), (rest) => ({ ...filters, teams: rest.map(Number) }));
  }
  if (wanted.includes("reps")) add("Rep", filters.reps, repOptions(options), (rest) => ({ ...filters, reps: rest }));
  if (wanted.includes("skills")) add(tab === "reps" ? "Skill" : "Needs", filters.skills, skillOptions(options), (rest) => ({ ...filters, skills: rest }));
  if (wanted.includes("firms")) {
    add("Firm", filters.firms.map(String), firmOptions(options), (rest) => ({ ...filters, firms: rest.map(Number) }));
  }
  if (wanted.includes("kinds")) add("Type", filters.kinds, KIND_OPTIONS, (rest) => ({ ...filters, kinds: rest as CallKind[] }));
  if (wanted.includes("modes")) add("Mode", filters.modes, MODE_OPTIONS, (rest) => ({ ...filters, modes: rest as DialMode[] }));
  const hours = hoursSummary(filters);
  if (hours) chips.push({ key: "hours", label: hours, remove: () => onChange({ ...filters, weekdays: [], hourFrom: null, hourTo: null }) });
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-border bg-gray-2 px-3 py-2 dark:bg-gray-1">
      {chips.map((c) => (
        <span key={c.key} className="inline-flex h-6 items-center gap-1 rounded-full border border-accent-6 bg-accent-2 pl-2.5 pr-1 text-xs text-accent-12">
          <span className="max-w-56 truncate">{c.label}</span>
          <button type="button" onClick={c.remove} aria-label={`Remove ${c.label}`} className="rounded-full p-0.5 text-accent-11 hover:bg-accent-4">
            <X className="size-3" />
          </button>
        </span>
      ))}
      <button type="button" onClick={onReset} className="text-xs text-text-muted underline-offset-2 hover:text-text hover:underline">
        Clear filters
      </button>
    </div>
  );
}

// --- saved views, export, share ----------------------------------------------------------------

function SavedViews({
  screenName, views, onOpen, onSave, onDelete, onPin, saving,
}: {
  /** "line report": what the words in the menu and the dialog call this screen. */
  screenName: string;
  views: readonly SavedView[];
  onOpen: (view: SavedView) => void;
  onSave: (name: string, pinned: boolean) => Promise<void>;
  onDelete: (id: string) => void;
  onPin: (id: string | null) => void;
  saving: boolean;
}) {
  const [naming, setNaming] = React.useState(false);
  const [name, setName] = React.useState("");
  const [pinned, setPinned] = React.useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="md" aria-label="Saved views">
            <Bookmark />
            <span className="hidden sm:inline">Views</span>
            {views.length > 0 && <span className="rounded bg-gray-3 px-1 text-2xs text-text-muted">{views.length}</span>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {views.length === 0 && (
            <p className="px-2 pb-2 text-xs text-text-subtle">
              Save the filters you use most on the {screenName}, and open them in one click.
            </p>
          )}
          {views.map((v) => (
            <DropdownMenuItem key={v.id} onSelect={() => onOpen(v)} className="group">
              <span className="min-w-0 flex-1">
                <span className="block truncate">{v.name}</span>
                {v.pinned && <span className="block text-2xs text-text-subtle">Opens by default</span>}
              </span>
              <button
                type="button"
                aria-label={v.pinned ? `Stop opening ${v.name} by default` : `Open ${v.name} by default`}
                title={v.pinned ? "Stop opening it by default" : "Open it by default"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPin(v.pinned ? null : v.id);
                }}
                className="rounded p-1 text-text-subtle hover:bg-surface-hover hover:text-text"
              >
                {v.pinned ? <PinOff /> : <Pin />}
              </button>
              <button
                type="button"
                aria-label={`Delete ${v.name}`}
                title="Delete"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(v.id);
                }}
                className="rounded p-1 text-text-subtle hover:bg-danger-2 hover:text-danger-11"
              >
                <Trash2 />
              </button>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setName("");
              setPinned(false);
              setNaming(true);
            }}
          >
            <Save />
            Save this view…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={naming} onOpenChange={setNaming}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Save this view</DialogTitle>
            <DialogDescription>
              The {screenName} with every filter as it is now. Saved to your account, for you only; use Copy
              link to share one.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              // A failed save keeps the dialog open; the page has said why in a toast.
              void onSave(name, pinned).then(() => setNaming(false), () => undefined);
            }}
          >
            <DialogBody className="space-y-3">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Spanish line, weekdays" aria-label="Name" maxLength={60} />
              <label className="flex items-center gap-2 text-sm text-text">
                <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="size-3.5 accent-[var(--color-accent-9)]" />
                Open this view when I open the {screenName}
              </label>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setNaming(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="default" loading={saving} disabled={!name.trim()}>
                Save view
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ExportMenu({ exports, nameParts }: { exports: readonly ReportExport[]; nameParts: readonly string[] }) {
  const toast = useToast();
  const sorted = [...exports].sort((a, b) => a.label.localeCompare(b.label));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" aria-label="Export">
          <Download />
          <span className="hidden sm:inline">Export</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuItem
          disabled={exports.length === 0}
          onSelect={() => {
            const zip = zipFiles(sorted.map((e) => ({ name: e.filename, content: e.csv() })));
            saveBlob(new Blob([zip], { type: "application/zip" }), exportName(nameParts, "zip"));
            toast.success("Export downloaded", `${sorted.length} tables in one ZIP`);
          }}
        >
          <FileArchive />
          Every table, as a ZIP of CSVs
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => window.print()}>
          <Printer />
          Print, or save as PDF
        </DropdownMenuItem>
        {sorted.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>One table, as CSV</DropdownMenuLabel>
            <div className="max-h-64 overflow-y-auto scrollbar-subtle">
              {sorted.map((e) => (
                <DropdownMenuItem
                  key={e.id}
                  onSelect={() => saveBlob(new Blob([e.csv()], { type: "text/csv;charset=utf-8" }), e.filename)}
                >
                  <Download />
                  {e.label}
                </DropdownMenuItem>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CopyLink() {
  const toast = useToast();
  return (
    <Button
      variant="outline"
      aria-label="Copy link"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          toast.success("Link copied", "Anyone who can open reports sees these filters.");
        } catch {
          window.prompt("Copy this link:", window.location.href);
        }
      }}
    >
      <Link2 />
      <span className="hidden sm:inline">Copy link</span>
    </Button>
  );
}

// --- the page's actions ------------------------------------------------------------------------

/** What a report screen offers about itself, in its header: its saved views, its link, its export.
 *  Not among the filters: none of them narrows anything. */
export function ReportActions({
  screenName, exports, nameParts, views, onOpenView, onSaveView, onDeleteView, onPinView, savingView,
}: {
  screenName: string;
  exports: readonly ReportExport[];
  nameParts: readonly string[];
  views: readonly SavedView[];
  onOpenView: (view: SavedView) => void;
  onSaveView: (name: string, pinned: boolean) => Promise<void>;
  onDeleteView: (id: string) => void;
  onPinView: (id: string | null) => void;
  savingView: boolean;
}) {
  return (
    <div className="report-print-hide flex items-center gap-2">
      <SavedViews
        screenName={screenName}
        views={views}
        onOpen={onOpenView}
        onSave={onSaveView}
        onDelete={onDeleteView}
        onPin={onPinView}
        saving={savingView}
      />
      <CopyLink />
      <ExportMenu exports={exports} nameParts={nameParts} />
    </div>
  );
}

// --- the filter panel ----------------------------------------------------------------------------

/**
 * Every filter, in one panel under the header: the period on the first row (when, compared with
 * what, grouped how, on whose clock, which hours), what to narrow to on the second, and the chips
 * that undo a narrowing one at a time under both.
 */
export function ReportFilterPanel({
  tab, filters, onChange, onReset, options,
}: {
  tab: ReportTab;
  filters: ReportFilters;
  onChange: Setter;
  onReset: () => void;
  options: ReportOptions | undefined;
}) {
  return (
    <section aria-label="Filters" className="report-print-hide rounded-lg border border-border bg-surface shadow-xs">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
        <DateRangeControl filters={filters} onChange={onChange} />
        <Select value={filters.compare} onValueChange={(v) => onChange({ ...filters, compare: v as CompareMode })}>
          <SelectTrigger className="w-auto min-w-40 text-sm" aria-label="Compare with">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="previous">Compare: period before</SelectItem>
            <SelectItem value="week">Compare: a week earlier</SelectItem>
            <SelectItem value="off">No comparison</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.granularity} onValueChange={(v) => onChange({ ...filters, granularity: v as Granularity })}>
          <SelectTrigger className="w-auto min-w-28 text-sm" aria-label="Group by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRANULARITIES.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.id === "auto" ? "Group: auto" : g.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.tz} onValueChange={(v) => onChange({ ...filters, tz: v })}>
          <SelectTrigger className="w-auto min-w-44 text-sm" aria-label="Time zone">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {zoneChoices(filters.tz).map((z) => (
              <SelectItem key={z.id} value={z.id}>
                {z.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <HoursControl filters={filters} onChange={onChange} />
        {tab === "lines" && (
          <Select value={String(filters.sla)} onValueChange={(v) => onChange({ ...filters, sla: Number(v) })}>
            <SelectTrigger className="w-auto min-w-36 text-sm" aria-label="Service level target">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 15, 20, 30, 45, 60, 90, 120].map((sec) => (
                <SelectItem key={sec} value={String(sec)}>
                  Answer within {sec} s
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2.5">
        <span className="mr-1 text-2xs font-medium uppercase tracking-wide text-text-subtle">Narrow to</span>
        <DimensionFilters tab={tab} filters={filters} onChange={onChange} options={options} />
      </div>
      <ActiveChips tab={tab} filters={filters} onChange={onChange} options={options} onReset={onReset} />
    </section>
  );
}
